import React, { useEffect, useMemo, useState } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import useScreenWidth from '~/hooks/useScreenWidth';

// Actions
import {
  fetchOneWebhook,
  fetchWebhookEvents,
  fetchWebhookLogs,
} from '~/redux/actions/webhooks-actions';
import { success } from '~/redux/actions/snackbar-actions';

// Utils
import { copyToClipboard } from '~/utils/helperFunctions';
import { dateFormat } from '~/utils/date-format';
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGINATION } from '~/utils/constants';

// Components
import DeleteWebhookModal from '~/components/Settings/Webhooks/DeleteWebhookModal';
import ActionModal from '~/components/Settings/Webhooks/ActionModal';
import TestWebhookModal from '~/components/Settings/Webhooks/TestWebhookModal';
import WebhookLog from '~/components/Settings/Webhooks/WebhookLog';
import WebhookLogTable from '~/components/Settings/Webhooks/WebhookLogTable';
import { buildAdditionalFieldOptions, getSelectedFieldLabels } from '../additionalFields';
import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcButton from '~/components/General/OlcButton';
import OlcPageBackButton from '~/components/General/OlcPageBackButton';
import Loader from '~/components/General/Loader';

// MUI Components
import { Box, GlobalStyles, IconButton, MenuItem, Tooltip, Typography } from '@mui/material';

// Icons
import {
  HideWebhookCIcon,
  ShowWebhookCIcon,
  CopyIconCIcon,
  DetailsEditCIcon,
  CheckmarkCIcon,
  DisableCIcon,
  TrashCIcon,
  ResendCIcon,
  PaymentTrashCIcon,
} from '~/assets/images';

// Styles
import './styles.scss';

// Read-only list of selected values, or a dash when there are none.
const WebhookDetailsTags = ({ items }) =>
  items.length ? (
    <Box component="dd" className="webhookDetailsTags">
      {items.map((item) => (
        <span key={item} className="webhookDetailsTag">
          {item}
        </span>
      ))}
    </Box>
  ) : (
    <Typography component="dd"> -</Typography>
  );

const WebhookDetails = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const webhookId = params.id;
  const navigatedWebhook =
    location.state?.webhook && String(location.state.webhook.id) === String(webhookId)
      ? location.state.webhook
      : null;

  const [selectedWebhook, setSelectedWebhook] = useState(navigatedWebhook);
  const [detailsLoading, setDetailsLoading] = useState(!navigatedWebhook);
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [deleteWebhookModal, setDeleteWebhookModal] = useState(false);
  const [selectedWebhookLog, setSelectedWebhookLog] = useState(null);
  const [displayLogPage, setDisplayLogPage] = useState(false);
  const [actionWebhookModal, setActionWebhookModal] = useState(false);
  const [testWebhookModal, setTestWebhookModal] = useState(false);
  const [showSecrectKey, setShowSecrectKey] = useState(false);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const events = useSelector((state) => state.webhooksReducers.events);
  const customFields = useSelector((state) => state.customFieldsReducers.allCustomFields);

  // Event titles come from the events list; fall back to the raw event name if it isn't known.
  const eventTitles = useMemo(
    () =>
      (selectedWebhook?.eventTypes || []).map(
        (eventType) => events.find(({ event }) => event === eventType)?.title || eventType
      ),
    [selectedWebhook, events]
  );
  // Additional fields only apply to events that carry a contact, so hide the row otherwise.
  const showAdditionalFields = events.some(
    ({ event, supportsAdditionalFields }) =>
      supportsAdditionalFields && selectedWebhook?.eventTypes?.includes(event)
  );
  const additionalFieldLabels = useMemo(
    () => getSelectedFieldLabels(selectedWebhook, buildAdditionalFieldOptions(customFields)),
    [selectedWebhook, customFields]
  );

  const onEdit = () => {
    navigate(`/settings/webhooks/update/${webhookId}`);
  };

  const onTest = () => {
    setTestWebhookModal(true);
  };

  const refreshWebhookLogs = () => {
    // Refresh logs after test webhook at multiple intervals for reliability
    setTimeout(() => {
      getWebhookLog();
    }, 2000);

    setTimeout(() => {
      getWebhookLog();
    }, 4000);

    setTimeout(() => {
      getWebhookLog();
    }, 6000);
  };

  const copyKey = (key) => {
    copyToClipboard(key);
    dispatch(success(MESSAGES.SETTINGS.API_KEYS.COPY_KEY));
  };

  const getOneWebhook = async () => {
    const shouldShowLoader = !selectedWebhook;

    if (shouldShowLoader) {
      setDetailsLoading(true);
    }

    try {
      const webbhook = await dispatch(fetchOneWebhook(webhookId));
      if (webbhook.status === 200) {
        setSelectedWebhook(webbhook.data.data);
      }
    } finally {
      if (shouldShowLoader) {
        setDetailsLoading(false);
      }
    }
  };

  const getWebhookLog = async () => {
    const payload = {
      webhookId: webhookId,
      ...pagination,
    };
    setLoading(true);
    const logs = await dispatch(fetchWebhookLogs(payload));
    if (logs.status === 200) {
      setWebhookLogs(logs.data.data);
    }
    setLoading(false);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isScreen = useScreenWidth(1200);

  useEffect(() => {
    getOneWebhook();
  }, [actionWebhookModal, webhookId]);

  useEffect(() => {
    if (!events.length) dispatch(fetchWebhookEvents());
  }, []);

  useEffect(() => {
    getWebhookLog();
  }, [webhookId, pagination]);

  return (
    <>
      {!displayLogPage ? (
        <>
          <GlobalStyles styles={{ body: { backgroundColor: '#f9fafb' } }} />
          <Box className="olcWrapper webhookDetailsPageWrapper">
            <Box className="olcScreenCard webhookDetailsScreenCard">
              <Box className="webhookDetailsHeader">
                <OlcPageBackButton onClick={() => navigate('/settings/webhooks')}>
                  {MESSAGES.SETTINGS.WEBHOOKS.DETAILS.BACK_BUTTON}
                </OlcPageBackButton>

                {!isScreen && selectedWebhook ? (
                  <Box className="webhookActions">
                    <OlcButton
                      id="webhook-details-actions"
                      variant="primary"
                      dropdown
                      expanded={open}
                      aria-controls={open ? 'webhook-details-menu' : undefined}
                      aria-haspopup="true"
                      onClick={handleClick}
                    >
                      Actions
                    </OlcButton>
                    <OlcActionMenu
                      id="webhook-details-menu"
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                      MenuListProps={{ 'aria-labelledby': 'webhook-details-actions' }}
                    >
                      <MenuItem
                        onClick={() => {
                          handleClose();
                          setDeleteWebhookModal(true);
                        }}
                      >
                        <img src={PaymentTrashCIcon} alt="" />
                        <Typography>
                          {MESSAGES.SETTINGS.WEBHOOKS.DETAILS.DELETE_BTN_TEXT}
                        </Typography>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleClose();
                          onTest();
                        }}
                      >
                        <img src={ResendCIcon} alt="" />
                        <Typography>Test</Typography>
                      </MenuItem>
                      {selectedWebhook?.status && (
                        <MenuItem
                          onClick={() => {
                            handleClose();
                            setActionWebhookModal(true);
                          }}
                        >
                          <img
                            src={
                              selectedWebhook?.status === 'Enabled' ? DisableCIcon : CheckmarkCIcon
                            }
                            alt=""
                          />
                          <Typography>
                            {selectedWebhook?.status === 'Enabled'
                              ? MESSAGES.SETTINGS.WEBHOOKS.DETAILS.DISABLE_LABEL
                              : MESSAGES.SETTINGS.WEBHOOKS.DETAILS.ENABLE_LABEL}
                          </Typography>
                        </MenuItem>
                      )}
                      <MenuItem
                        onClick={() => {
                          handleClose();
                          onEdit();
                        }}
                      >
                        <img src={DetailsEditCIcon} alt="" />
                        <Typography>{MESSAGES.SETTINGS.WEBHOOKS.DETAILS.EDIT_BTN_TEXT}</Typography>
                      </MenuItem>
                    </OlcActionMenu>
                  </Box>
                ) : isScreen && selectedWebhook ? (
                  <Box className="webhookDetailsActionBtn">
                    <OlcButton
                      variant="secondary"
                      startIcon={<img src={TrashCIcon} alt="" />}
                      onClick={() => setDeleteWebhookModal(true)}
                    >
                      {MESSAGES.SETTINGS.WEBHOOKS.DETAILS.DELETE_BTN_TEXT}
                    </OlcButton>
                    <OlcButton
                      variant="secondary"
                      startIcon={<img src={ResendCIcon} alt="" />}
                      onClick={onTest}
                    >
                      Test
                    </OlcButton>
                    {selectedWebhook?.status && (
                      <OlcButton
                        variant="secondary"
                        startIcon={
                          <img
                            src={
                              selectedWebhook?.status === 'Enabled' ? DisableCIcon : CheckmarkCIcon
                            }
                            alt=""
                          />
                        }
                        onClick={() => setActionWebhookModal(true)}
                      >
                        {selectedWebhook?.status === 'Enabled'
                          ? MESSAGES.SETTINGS.WEBHOOKS.DETAILS.DISABLE_LABEL
                          : MESSAGES.SETTINGS.WEBHOOKS.DETAILS.ENABLE_LABEL}
                      </OlcButton>
                    )}
                    <OlcButton
                      variant="secondary"
                      startIcon={<img src={DetailsEditCIcon} alt="" />}
                      onClick={onEdit}
                    >
                      {MESSAGES.SETTINGS.WEBHOOKS.DETAILS.EDIT_BTN_TEXT}
                    </OlcButton>
                  </Box>
                ) : null}
              </Box>

              {detailsLoading ? (
                <Box
                  className="webhookDetailsLoading"
                  role="status"
                  aria-label="Loading webhook details"
                >
                  <Loader />
                </Box>
              ) : (
                <>
                  <Box className="webhookDetailsSection">
                    <Typography className="webhookDetailsTitle">
                      {MESSAGES.SETTINGS.WEBHOOKS.DETAILS.TITLE}
                    </Typography>
                    <Box component="dl" className="webhookDetailsFields">
                      <Box className="webhookDetailsField webhookDetailsFieldWide">
                        <Typography component="dt">
                          {MESSAGES.SETTINGS.WEBHOOKS.DETAILS.URL_LABEL}
                        </Typography>
                        <Typography component="dd" className="webhookDetailsBreakWord">
                          {selectedWebhook?.callbackUrl || ' -'}
                        </Typography>
                      </Box>
                      <Box className="webhookDetailsField">
                        <Typography component="dt">
                          {MESSAGES.SETTINGS.WEBHOOKS.DETAILS.DESCRIPTION_LABEL}
                        </Typography>
                        <Typography component="dd">{selectedWebhook?.topic || ' -'}</Typography>
                      </Box>
                      <Box className="webhookDetailsField">
                        <Typography component="dt">
                          {MESSAGES.SETTINGS.WEBHOOKS.DETAILS.STATUS_LABEL}
                        </Typography>
                        <Typography component="dd">{selectedWebhook?.status || ' -'}</Typography>
                      </Box>
                      <Box className="webhookDetailsField webhookDetailsFieldWide">
                        <Typography component="dt">
                          {MESSAGES.SETTINGS.WEBHOOKS.DETAILS.SLACK_INCOMING_WEBHOOK}
                        </Typography>
                        <Typography component="dd" className="webhookDetailsBreakWord">
                          {selectedWebhook?.slackWebhookUrl || ' -'}
                        </Typography>
                      </Box>
                      <Box className="webhookDetailsField">
                        <Typography component="dt">
                          {MESSAGES.SETTINGS.WEBHOOKS.DETAILS.SECRET_KEY_LABEL}
                        </Typography>
                        {selectedWebhook?.secret ? (
                          <Box component="dd" className="webhookDetailsSecret">
                            <input
                              aria-label="Webhook secret key"
                              type={showSecrectKey ? 'text' : 'password'}
                              value={selectedWebhook.secret}
                              readOnly
                            />
                            <Tooltip title={showSecrectKey ? 'Hide secret key' : 'Show secret key'}>
                              <IconButton
                                aria-label={showSecrectKey ? 'Hide secret key' : 'Show secret key'}
                                onClick={() => setShowSecrectKey((current) => !current)}
                              >
                                <img
                                  src={showSecrectKey ? HideWebhookCIcon : ShowWebhookCIcon}
                                  alt=""
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copy secret key">
                              <IconButton
                                aria-label="Copy secret key"
                                onClick={() => copyKey(selectedWebhook.secret)}
                              >
                                <img src={CopyIconCIcon} alt="" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography component="dd"> -</Typography>
                        )}
                      </Box>
                      <Box className="webhookDetailsField">
                        <Typography component="dt">
                          {MESSAGES.SETTINGS.WEBHOOKS.DETAILS.CUSTOM_HEADERS_LABEL}
                        </Typography>
                        <Typography component="dd" className="webhookDetailsBreakWord">
                          {selectedWebhook?.customHeaders || ' -'}
                        </Typography>
                      </Box>
                      <Box className="webhookDetailsField">
                        <Typography component="dt">
                          {MESSAGES.SETTINGS.WEBHOOKS.DETAILS.EVENT_TYPES_LABEL}
                        </Typography>
                        <WebhookDetailsTags items={eventTitles} />
                      </Box>
                      {showAdditionalFields && (
                        <Box className="webhookDetailsField">
                          <Typography component="dt">
                            {MESSAGES.SETTINGS.WEBHOOKS.DETAILS.ADDITIONAL_FIELDS_LABEL}
                          </Typography>
                          <WebhookDetailsTags items={additionalFieldLabels} />
                        </Box>
                      )}
                      <Box className="webhookDetailsField">
                        <Typography component="dt">
                          {MESSAGES.SETTINGS.WEBHOOKS.DETAILS.CREATED_DATE_LABEL}
                        </Typography>
                        <Typography component="dd">
                          {dateFormat(selectedWebhook?.createdAt) || ' -'}
                        </Typography>
                      </Box>
                      <Box className="webhookDetailsField">
                        <Typography component="dt">
                          {MESSAGES.SETTINGS.WEBHOOKS.DETAILS.LAST_USED_DATE_LABEL}
                        </Typography>
                        <Typography component="dd">
                          {dateFormat(selectedWebhook?.lastAccessAt) || ' -'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <WebhookLogTable
                    rows={webhookLogs}
                    pagination={pagination}
                    setPagination={setPagination}
                    setSelectedWebhookLog={setSelectedWebhookLog}
                    setDisplayLogPage={setDisplayLogPage}
                    loading={loading}
                    webhookId={webhookId}
                  />
                </>
              )}
            </Box>
            <ActionModal
              openModal={actionWebhookModal}
              handleCloseModal={() => setActionWebhookModal(false)}
              selectedWebhook={selectedWebhook}
            />
            <DeleteWebhookModal
              openModal={deleteWebhookModal}
              handleCloseModal={() => setDeleteWebhookModal(false)}
              selectedWebhook={webhookId}
            />
            <TestWebhookModal
              openModal={testWebhookModal}
              handleCloseModal={() => setTestWebhookModal(false)}
              webhookId={webhookId}
              onTestComplete={refreshWebhookLogs}
            />
          </Box>
        </>
      ) : (
        <WebhookLog selectedWebhookLog={selectedWebhookLog} setDisplayLogPage={setDisplayLogPage} />
      )}
    </>
  );
};

export default WebhookDetails;
